/*
  Mapper
  A canvas-powered document builder to map form elements onto
*/

import { cloneDeep } from 'lodash-es';
import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { debounce, map } from 'rxjs/operators';
import { forkJoin, fromEvent, merge, Observable, timer } from 'rxjs';
import { ID } from '@datorama/akita';

import {
  DocumentFieldDeletionStatus,
  DocumentService,
  EditorState,
  FormService,
  MapperService
} from '@app/shared/services';
import { PageObject } from '@state/document';
import { FormElement, PageObjectType, SelectableType } from '@state/form';
import { MappingCheckBox, MappingCheckBoxOptions } from './mapping-check-box/mapping-check-box';
import { MappingTextBox } from './mapping-text-box/mapping-text-box';
import { appearance } from './mapper.appearance';

declare let fabric: any;

@Component({
  exportAs: 'fl-mapper',
  selector: 'fl-mapper',
  styleUrls: ['./mapper.component.scss'],
  templateUrl: './mapper.component.html'
})
export class MapperComponent implements OnInit, OnDestroy {
  @Input() width: string;
  @Input() height: string;
  @Input() id: string;
  @Input() page: any;
  @ViewChild('documentSheet', { static: true }) documentSheet: ElementRef;
  @ViewChild('addFieldButton') addFieldButton: ElementRef;

  private deletedPageObjects = [];
  private deletedCanvasObjects = [];

  clipboard: MappingTextBox | MappingCheckBox;
  canvas: any;
  data: any;
  fieldCount: number;
  guideIsShown: boolean;
  signatureHeight = '0.032150773';
  textBoxHeight = '0.025150773';
  textBoxOffsetTop = 0;
  xGuide: any;
  yGuide: any;

  constructor(
    private documentService: DocumentService,
    private editorState: EditorState,
    private formService: FormService,
    private mapperService: MapperService
  ) {
    this.fieldCount = 0;
  }

  @HostListener('mouseup', ['$event'])
  onMouseup(event: MouseEvent): void {
    switch (this.documentService.fieldButtonSelected) {
      case PageObjectType.Text:
      case PageObjectType.Date:
      case PageObjectType.FixedText:
        this.createTextbox(event, this.documentService.fieldButtonSelected);
        break;
      case PageObjectType.Checkbox:
        this.createCheckbox(event);
        break;
      case PageObjectType.CheckboxSpecial:
        this.createCheckbox(event, true);
        break;
      case PageObjectType.Signature:
        this.createSignature(event);
        break;
    }
    this.xGuide.bringToFront();
    this.yGuide.bringToFront();

    delete this.documentService.fieldButtonSelected;
  }

  init(width: any, height: any): void {
    this.width = width;
    this.height = height;

    this.xGuide = new fabric.Line([0, 0, width, 0], {
      opacity: 0,
      stroke: appearance.guideColor,
      strokeDashArray: [5, 2],
      strokeWidth: 1.5
    });
    this.yGuide = new fabric.Line([0, 0, 0, height], {
      opacity: 0,
      stroke: appearance.guideColor,
      strokeDashArray: [5, 2],
      strokeWidth: 1.5
    });
    this.canvas = new fabric.Canvas(this.documentSheet.nativeElement, {
      borderScaleFactor: 2,
      height,
      imageSmoothingEnabled: false,

      // Selecting styles
      selectionBorderColor: appearance.selectionBorderColor,
      selectionColor: appearance.selectionBackgroundColor,
      selectionFullyContained: true,
      selectionLineWidth: 2,
      width
    });
    this.mapperService.addMapper(this);
    this.canvas.add(this.xGuide);
    this.canvas.add(this.yGuide);

    // Selected styles
    fabric.Group.prototype.borderScaleFactor = 3;
    fabric.Group.prototype.borderColor = appearance.selectedBorderColor;

    fabric.Object.prototype.transparentCorners = false;
    this.renderFields();
    this.bindEvents();
  }

  ngOnInit() {}

  ngOnDestroy() {
    this.mapperService.reset();
  }

  get pageObjects(): PageObject[] {
    let page = this.documentService.document.pages.filter(
      (documentPage) => documentPage.id === this.page.id
    )[0];
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return page.page_objects;
  }

  addCheckBox(params: Partial<MappingCheckBoxOptions>, pageObject: PageObject): void {
    let id: ID = params.id || pageObject.id;
    let scaleX: number = params.height;
    let { clientX, clientY, width, height, counterSign } = params;
    let options = { clientX, clientY, counterSign, height, id, scaleX, width };
    this.documentService.mapperObjects[id] = new MappingCheckBox(options, id, this);

    this.canvas.add(this.documentService.mapperObjects[id]);
    this.canvas.renderAll();
  }

  addTextBox(params: any, pageObject: PageObject): void {
    let label: string = params.label || pageObject?.field_label;
    let id: string = params.id || pageObject?.id;
    let { clientX, clientY, width, height, counterSign, fixedText } = params;

    this.documentService.mapperObjects[id] = new MappingTextBox(
      label,
      {
        counterSign,
        fixedText,
        height,
        left: clientX,
        scaleX: width,
        top: clientY
      },
      id,
      this
    );

    this.documentService.mapperObjects[id].reRender.subscribe({
      next: () => {
        this.canvas.requestRenderAll();
      }
    });
    this.canvas.add(this.documentService.mapperObjects[id]);
  }

  bindEvents(): void {
    this.canvas.on('selection:cleared', this.setPageObjectDeSelected);
    this.canvas.on('selection:created', this.showGuide);
    this.canvas.on('selection:updated', this.showGuide);
    this.canvas.on('object:moving', this.moveField);
    this.canvas.on('object:modified', this.modifyPageObject);
    this.bindKeypressEvent$().subscribe({ next: ($event: KeyboardEvent) => this.onKeyPress($event) });
  }

  modifyPageObject = (): void => {
    const object = this.canvas.getActiveObject();
    let params = {
      left: object.left / this.canvas.width,
      top: object.top / this.canvas.height,
      width: object.scaleX / this.canvas.width
    };

    let id: ID =
      this.canvas.getActiveObject().id ||
      // eslint-disable-next-line no-underscore-dangle
      this.canvas.getActiveObject()._objects[0].id ||
      // eslint-disable-next-line no-underscore-dangle
      this.canvas.getActiveObject()._objects[0].outerBox.id ||
      this.canvas.getActiveObject();
    this.documentService.updateAndSetPageObject$(this.documentService.document.id, id, params).subscribe();
  };

  bindKeypressEvent$(): Observable<KeyboardEvent> {
    const eventsType$ = [fromEvent(window, 'keypress'), fromEvent(window, 'keydown')];
    // we merge all kind of event as one observable.
    return merge(...eventsType$).pipe(
      // We prevent multiple next by wait 10ms before to next value.
      debounce(() => timer(10)),
      // We map answer to KeyboardEvent, typescript strong typing...
      map((state) => state as KeyboardEvent)
    );
  }

  copy(): void {
    if (this.canvas.getActiveObject()) this.clipboard = this.canvas.getActiveObject();
  }

  createTextbox(event: any, object_type: PageObjectType = PageObjectType.Text): void {
    let params = {
      clientX: event.layerX,
      clientY: event.layerY,
      counterSign: false,
      fixedText: ['fixed_text', 'date'].includes(object_type),
      height: 26,
      width: 250
    };

    let pageObject = {
      document_id: this.documentService.document.id,
      document_page_id: this.page.id,
      height: null,
      left: event.layerX / this.canvas.width,
      object_type,
      top: event.layerY / this.canvas.height,
      width: 0.35
    };

    this.documentService.createPageObject$(pageObject).subscribe({
      next: (data: PageObject[]) => {
        let formId: ID;

        for (let newPageObject of data) {
          let { left } = newPageObject;
          if (typeof left === 'string') left = parseFloat(left);
          params.clientX = left * this.canvas.width;
          this.addTextBox(params, newPageObject);
          this.pageObjects.push(newPageObject);

          formId = newPageObject.form_id;
        }

        this.formService.getForm(formId);
      }
    });
  }

  createCheckbox(event: any, special: boolean = false): void {
    let params = {
      clientX: event.layerX,
      clientY: event.layerY,
      counterSign: false,
      height: 26.031050055,
      scaleX: 26.031050055,
      width: 26.031050055
    };

    let pageObject = {
      document_id: this.documentService.document.id,
      document_page_id: this.page.id,
      height: null,
      left: event.layerX / this.canvas.width,
      object_type: special ? PageObjectType.CheckboxSpecial : PageObjectType.Checkbox,
      top: event.layerY / this.canvas.height,
      width: 0.35
    };

    this.documentService.createPageObject$(pageObject).subscribe({
      next: (pageObjects: PageObject[]) => {
        let formId: ID;

        for (let newPageObject of pageObjects) {
          let { left } = newPageObject;
          if (typeof left === 'string') left = parseFloat(left);
          params.clientX = left * this.canvas.width;
          this.addCheckBox(params, newPageObject);
          this.pageObjects.push(newPageObject);

          formId = newPageObject.form_id;
        }

        this.formService.getForm(formId);
      }
    });
  }

  createSignature(event: any): void {
    let params = {
      clientX: event.layerX,
      clientY: event.layerY,
      counterSign: false,
      height: 34,
      width: 250
    };

    let pageObject = {
      document_id: this.documentService.document.id,
      document_page_id: this.page.id,
      height: null,
      left: event.layerX / this.canvas.width,
      object_type: PageObjectType.Signature,
      top: event.layerY / this.canvas.height,
      width: 0.35
    };

    this.documentService.createPageObject$(pageObject).subscribe({
      next: (data: PageObject[]) => {
        let formId: ID;

        for (let newPageObject of data) {
          let { left } = newPageObject;
          if (typeof left === 'string') left = parseFloat(left);
          params.clientX = left * this.canvas.width;
          this.addTextBox(params, newPageObject);
          this.pageObjects.push(newPageObject);

          formId = newPageObject.form_id;
        }

        this.formService.getForm(formId);
      }
    });
  }

  delete(): void {
    let selectedObjects = this.canvas.getActiveObjects();
    if (selectedObjects.length === 0) return;

    this.documentService.updateDeletionState([DocumentFieldDeletionStatus.Deleting, '']);

    let requests: Observable<null>[] = [];

    for (let object of selectedObjects) {
      let id: ID = object.id || object.outerBox?.id;

      if (id) requests.push(this.documentService.deletePageObject$(this.documentService.document.id, id));
    }

    forkJoin(requests).subscribe({
      error: () => {
        this.documentService.updateDeletionState([
          DocumentFieldDeletionStatus.Failed,
          'Failed to delete elements due to a server error. Please try again.'
        ]);
      },
      next: () => {
        for (let object of selectedObjects) {
          let id: ID = object.id || object.outerBox.id;
          let [pageObject]: PageObject[] | undefined = this.pageObjects.filter(
            (obj: PageObject) => obj.id === id
          );
          let index = this.pageObjects.findIndex((element: PageObject) => element.id === pageObject?.id);
          this.deletedCanvasObjects.push(object);
          this.canvas.remove(object);
          this.deletedPageObjects.push(object);
          this.pageObjects.splice(index, 1);
        }
        this.documentService.updateDeletionState([DocumentFieldDeletionStatus.NotDeleting, '']);
        this.deselectAll();
        this.formService.getForm(this.formService.form.id);
      }
    });
  }

  groupHasTextFields(group: any[]): number {
    for (let i = 0; i < group.length; i++) {
      if (!group[i].innerBox) return i;
      return -1;
    }
  }

  setPageObjectDeSelected = (): void => {
    delete this.editorState.selectedItemPageObject;
    this.editorState.selectedItem = this.documentService.document;

    this.hideGuide();
  };

  deselectAll(): void {
    this.canvas.discardActiveObject();
    this.canvas.requestRenderAll();
  }

  hideGuide(): void {
    if (!this.guideIsShown) return;
    this.xGuide.set({ opacity: 0 });
    this.yGuide.set({ opacity: 0 });
    this.guideIsShown = false;
    this.canvas.renderAll();
  }

  moveField = (event: any): void => {
    let selectedX;
    let selectedY = event.target;
    const obj = event.target;
    const objTop = obj.getBoundingRect().top;
    const objLeft = obj.getBoundingRect().left;
    const objHeight = obj.getBoundingRect().height;
    const objWidth = obj.getBoundingRect().width;

    // if object is too big ignore
    if (obj.currentHeight > obj.canvas.height || obj.currentWidth > obj.canvas.width) return;

    obj.setCoords();
    // top-left corner
    if (objTop < 0 || objLeft < 0) {
      obj.top = Math.max(obj.top, obj.top - objTop);
      obj.left = Math.max(obj.left, obj.left - objLeft);
    }
    // top-right corner
    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
    if (objTop + objHeight > obj.canvas.height || objLeft + objWidth > obj.canvas.width) {
      // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
      obj.top = Math.min(obj.top, obj.canvas.height - objHeight + obj.top - objTop);
      // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
      obj.left = Math.min(obj.left, obj.canvas.width - objWidth + obj.left - objLeft);
    }
    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
    selectedY = obj.top + 20;
    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
    selectedX = obj.left + 7;
    this.xGuide.set({
      y1: selectedY,
      y2: selectedY
    });
    this.yGuide.set({
      x1: selectedX,
      x2: selectedX
    });
  };

  onKeyPress($event: KeyboardEvent): void {
    if (($event.ctrlKey || $event.metaKey) && $event.keyCode === 67) this.copy();
    if (($event.ctrlKey || $event.metaKey) && $event.keyCode === 86 && this.clipboard) this.paste();
  }

  assignCanvasObjectId(canvasObject: any, id: any): any {
    if (canvasObject.constructor.name === 'MappingTextBox') {
      canvasObject.id = id;
    } else {
      canvasObject.outerBox.id = id;
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return canvasObject;
  }

  setSelectedPageObject(id: ID): PageObject | undefined {
    let element: FormElement | undefined;
    let pageObject: PageObject | undefined = this.pageObjects.find((obj: PageObject) => obj.id === id);
    if (pageObject) element = this.formService.getElementByFieldId(pageObject.form_field_id);

    if (element) {
      this.editorState.selectedItem = element;
      this.editorState.selectedItem.selectableType = SelectableType.PageObject;
      this.editorState.selectedItemPageObject = pageObject;
    }
    return pageObject;
  }

  paste(): void {
    // clone again, so you can do multiple copies.
    if (!this.clipboard) return;

    let id: ID;
    let pageObjectToCopy: PageObject;

    switch (this.clipboard.constructor.name) {
      case 'MappingTextBox':
        let mappingTextBoxToCopy: Partial<MappingTextBox> = cloneDeep(this.clipboard);
        id = this.clipboard.id;
        pageObjectToCopy = this.setSelectedPageObject(id);
        if (pageObjectToCopy) {
          pageObjectToCopy.left = Number(pageObjectToCopy.left) + 4 / this.page.width;
          pageObjectToCopy.top = Number(pageObjectToCopy.top) + 45 / this.page.height;
          this.documentService.createPageObject$(pageObjectToCopy).subscribe({
            next: (data: PageObject[]) => {
              let [pageObject] = data;
              this.formService.getForm(pageObject.form_id);
              this.pageObjects.push(pageObject);
              let textBoxClone = new MappingTextBox(
                mappingTextBoxToCopy.label,
                {
                  counterSign: this.clipboard.counterSign,
                  fixedText: mappingTextBoxToCopy.fixedText,
                  height: mappingTextBoxToCopy.height,
                  left: mappingTextBoxToCopy.left + 30,
                  scaleX: mappingTextBoxToCopy.scaleX,
                  top: mappingTextBoxToCopy.top + 30
                },
                pageObject.id,
                this
              );
              this.canvas.add(textBoxClone);
              this.clipboard = cloneDeep(textBoxClone);
            }
          });
        }
        break;
      case 'MappingCheckBox':
        let mappingCheckBoxToCopy: Partial<MappingCheckBox> = cloneDeep(this.clipboard);
        let options: MappingCheckBoxOptions;
        id = this.clipboard.outerBox.id;
        pageObjectToCopy = this.setSelectedPageObject(id);
        if (pageObjectToCopy) {
          pageObjectToCopy.left = Number(pageObjectToCopy.left) + 45 / this.page.width;
          pageObjectToCopy.top = Number(pageObjectToCopy.top) + 45 / this.page.height;
          this.documentService.createPageObject$(pageObjectToCopy).subscribe({
            next: (data: PageObject[]) => {
              let [pageObject]: PageObject[] = data;
              this.formService.getForm(pageObject.form_id);
              this.pageObjects.push(pageObject);
              options = {
                clientX: mappingCheckBoxToCopy.outerBox.aCoords.bl.x + 28,
                clientY: mappingCheckBoxToCopy.outerBox.aCoords.bl.y,
                counterSign: mappingCheckBoxToCopy.counterSign,
                height: mappingCheckBoxToCopy.height,
                scaleX: mappingCheckBoxToCopy.scaleX,
                width: mappingCheckBoxToCopy.width
              };
              let checkBoxClone = new MappingCheckBox(options, pageObject.id, this);
              this.canvas.add(checkBoxClone);
              this.clipboard = cloneDeep(checkBoxClone);
            }
          });
        }
        break;
      default:
        break;
    }
  }

  renderFields(): void {
    this.canvas.clear();

    for (let field of this.pageObjects) {
      let { width: mapperWidth, height, textBoxHeight, textBoxOffsetTop, signatureHeight } = this;
      let { left, top, width } = field;
      if (typeof left === 'string') left = parseFloat(left);
      if (typeof top === 'string') top = parseFloat(top);
      if (typeof width === 'string') width = parseFloat(width);
      let fieldLeft = parseInt(mapperWidth, 10) * left;
      let fieldTop = parseInt(height, 10) * top + textBoxOffsetTop;
      let fieldWidth = parseInt(mapperWidth, 10) * width;
      let fieldHeight =
        parseInt(height, 10) *
        parseFloat(field.object_type === 'signature' ? signatureHeight : textBoxHeight);
      let fieldType =
        field.object_type === 'text' ||
        field.object_type === 'signature' ||
        field.object_type === 'fixed_text'
          ? 'TextBox'
          : 'CheckBox';
      let default_width = field.object_type === 'checkbox' ? 13 : 225;
      this['add' + fieldType]({
        clientX: fieldLeft,
        clientY: fieldTop,
        counterSign: field.counter_signable,
        fixedText: field.object_type === PageObjectType.FixedText,
        height: fieldHeight,
        id: field.id,
        label: field.field_label,
        width: fieldWidth || default_width
      });
    }
    this.xGuide.bringToFront();
    this.yGuide.bringToFront();
  }

  renderCanvas(): void {
    this.canvas.renderAll();
  }

  showGuide = (event: {
    e: MouseEvent;
    selected: MappingTextBox[] | MappingCheckBox[];
    target: MappingTextBox | MappingCheckBox;
  }): void => {
    let id: ID =
      this.canvas.getActiveObject().id ||
      // eslint-disable-next-line no-underscore-dangle
      this.canvas.getActiveObject()._objects[0].id ||
      // eslint-disable-next-line no-underscore-dangle
      this.canvas.getActiveObject()._objects[0].outerBox.id ||
      this.canvas.getActiveObject().outerBox.id;
    this.setSelectedPageObject(id);

    let i;
    let group;
    let selectedX;
    let selectedY;

    // removing controls if a group is selected
    // eslint-disable-next-line no-underscore-dangle
    if (this.canvas.getActiveObject()._objects) {
      event.target.setControlsVisibility({
        bl: false,
        br: false,
        mb: false,
        ml: false,
        mr: false,
        mt: false,
        mtr: false,
        tl: false,
        tr: false
      });
      // eslint-disable-next-line no-underscore-dangle
      this.groupHasTextFields(this.canvas.getActiveObject()._objects);
    }

    // if the selected fields have a field that should have a guide
    if (this.groupHasTextFields(event.selected) > -1) {
      this.guideIsShown = true;

      if (event.selected.length > 1) group = event.selected;

      // if a multiple fields are selected
      if (group) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        i = this.groupHasTextFields(group);
        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands,no-underscore-dangle
        selectedY = this.canvas._activeObject._objects[i].aCoords['tl'].y + 20;
        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands,no-underscore-dangle
        selectedX = this.canvas._activeObject._objects[i].aCoords['bl'].x + 7;
        // show/move guide after fields are aligned
      } else {
        let x: number = event.selected[0].left;
        let y: number = event.selected[0].top;
        selectedX = x + 7;
        selectedY = y + 20;
      }

      this.xGuide.set({
        opacity: 1,
        y1: selectedY,
        y2: selectedY
      });
      this.yGuide.set({
        opacity: 1,
        x1: selectedX,
        x2: selectedX
      });

      this.canvas.renderAll();
    } else {
      this.hideGuide();
    }
  };
}
